// @ts-nocheck
class JoyaPreviewCanvas {
  constructor() {
    this.states = [];
    this.resizeScheduled = false;

    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
    this.handleSlideshowSelect = this.handleSlideshowSelect.bind(this);
    this.handleResize = this.handleResize.bind(this);

    this.init();

    document.addEventListener('variant:update', this.handleVariantUpdate);
    document.addEventListener('slideshow:select', this.handleSlideshowSelect, true);
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('orientationchange', this.handleResize);
  }

  init() {
    document.querySelectorAll('.joya-wrapper[data-preview-enabled="true"]').forEach((wrapper) => {
      this.bindWrapper(wrapper);
    });
  }

  bindWrapper(wrapper) {
    if (!(wrapper instanceof HTMLElement)) return;
    if (wrapper.dataset.joyaPreviewBound === 'true') return;
    wrapper.dataset.joyaPreviewBound = 'true';

    const state = {
      wrapper,
      gallery: null,
      layer: null,
      canvas: null,
      targetContainer: null,
      targetImage: null,
      swapInfo: null,
      debounceTimer: null,
      onImageLoad: () => this.render(state),
      onInput: (e) => {
        const inputEl = e.target;
        if (inputEl instanceof HTMLInputElement || inputEl instanceof HTMLTextAreaElement) {
          this.enforceTextLimit(state, inputEl);
        }
        this.scheduleDebouncedRender(state);
      },
      onSync: () => {
        window.clearTimeout(state.debounceTimer);
        this.refreshTarget(state);
        this.render(state);
      },
    };

    wrapper.addEventListener('input', state.onInput);
    wrapper.addEventListener('joya:sync', state.onSync);

    this.states.push(state);
    this.refreshTarget(state);
    this.render(state);
  }

  unbindState(state) {
    window.clearTimeout(state.debounceTimer);
    state.wrapper.removeEventListener('input', state.onInput);
    state.wrapper.removeEventListener('joya:sync', state.onSync);
    if (state.targetImage) {
      state.targetImage.removeEventListener('load', state.onImageLoad);
    }
    this.detachLayer(state);
  }

  pruneDisconnectedStates() {
    this.states = this.states.filter((state) => {
      if (state.wrapper.isConnected) return true;
      this.unbindState(state);
      return false;
    });
  }

  handleVariantUpdate() {
    window.requestAnimationFrame(() => {
      this.pruneDisconnectedStates();
      this.init();
      this.states.forEach((state) => {
        this.refreshTarget(state);
        this.render(state);
      });
    });
  }

  handleSlideshowSelect(event) {
    const slide = event?.detail?.slide;
    if (!(slide instanceof Element)) return;
    this.states.forEach((state) => {
      if (!state.wrapper.isConnected || !state.gallery) return;
      if (!state.gallery.contains(slide)) return;
      this.refreshTarget(state, { skipSlideshowNav: true });
      this.render(state);
    });
  }

  handleResize() {
    if (this.resizeScheduled) return;
    this.resizeScheduled = true;
    window.requestAnimationFrame(() => {
      this.resizeScheduled = false;
      this.pruneDisconnectedStates();
      this.states.forEach((state) => {
        this.refreshTarget(state);
        this.render(state);
      });
    });
  }

  scheduleDebouncedRender(state) {
    window.clearTimeout(state.debounceTimer);
    state.debounceTimer = window.setTimeout(() => {
      this.refreshTarget(state);
      this.render(state);
    }, 180);
  }

  renderAll() {
    this.pruneDisconnectedStates();
    this.states.forEach((state) => {
      this.refreshTarget(state);
      this.render(state);
    });
  }

  refreshTarget(state, { skipSlideshowNav = false } = {}) {
    const section = state.wrapper.closest('.shopify-section');
    const gallery =
      (section && section.querySelector('media-gallery')) ||
      document.querySelector('media-gallery');

    if (!(gallery instanceof HTMLElement)) {
      this.detachLayer(state);
      state.gallery = null;
      return;
    }

    state.gallery = gallery;

    const gridVisible = this.isElementVisible(gallery.querySelector('.media-gallery__grid'));
    let nextTarget = null;

    const mediaId = state.wrapper.dataset.previewMediaId;
    if (mediaId) {
      const gridItem = gallery.querySelector(`.media-gallery__grid > .product-media-container[data-media-id="${mediaId}"]`);
      const slideshowItem = gallery.querySelector(`slideshow-component[ref="slideshow"] .product-media-container[data-media-id="${mediaId}"]`);
      nextTarget = gridVisible ? (gridItem || slideshowItem) : (slideshowItem || gridItem);
    }

    if (!nextTarget) {
      const configuredIndex = this.getTargetIndex(state.wrapper);
      const slideshowContainers = Array.from(
        gallery.querySelectorAll('slideshow-component[ref="slideshow"] slideshow-slide.product-media-container')
      );
      const gridContainers = Array.from(
        gallery.querySelectorAll('.media-gallery__grid > .product-media-container')
      );
      const slideshowTarget = this.pickTargetContainer(slideshowContainers, configuredIndex);
      const gridTarget = this.pickTargetContainer(gridContainers, configuredIndex);
      nextTarget = gridVisible ? gridTarget || slideshowTarget : slideshowTarget || gridTarget;
    }

    if (!(nextTarget instanceof HTMLElement)) {
      this.detachLayer(state);
      return;
    }

    this.attachLayer(state, nextTarget);
    if (!skipSlideshowNav) {
      this.navigateSlideshowToTarget(gallery, gridVisible, nextTarget);
    }
    this.swapGridItemToFirst(state, nextTarget, gallery);

    const nextImage = nextTarget.querySelector('img.product-media__image');
    if (state.targetImage !== nextImage) {
      if (state.targetImage) {
        state.targetImage.removeEventListener('load', state.onImageLoad);
      }
      state.targetImage = nextImage instanceof HTMLImageElement ? nextImage : null;
      if (state.targetImage) {
        state.targetImage.addEventListener('load', state.onImageLoad);
      }
    }
  }

  swapGridItemToFirst(state, targetContainer, gallery) {
    const grid = gallery.querySelector('.media-gallery__grid');
    if (!(grid instanceof HTMLElement)) return;

    const items = Array.from(grid.querySelectorAll(':scope > .product-media-container'));
    if (items.length <= 1 || items[0] === targetContainer || !items.includes(targetContainer)) {
      state.swapInfo = null;
      return;
    }

    const originalIndex = items.indexOf(targetContainer);
    state.swapInfo = { grid, originalIndex };
    grid.insertBefore(targetContainer, items[0]);
  }

  restoreGridOrder(state) {
    if (!state.swapInfo) return;

    const { grid, originalIndex } = state.swapInfo;
    state.swapInfo = null;

    if (!(grid instanceof HTMLElement)) return;

    const items = Array.from(grid.querySelectorAll(':scope > .product-media-container'));
    const swappedItem = items[0];
    if (!(swappedItem instanceof HTMLElement)) return;

    const refNode = items[originalIndex + 1] || null;
    grid.insertBefore(swappedItem, refNode);
  }

  getTargetIndex(wrapper) {
    const raw = this.toNumber(wrapper.dataset.previewTargetImageIndex, 1);
    return Math.max(0, Math.floor(raw) - 1);
  }

  pickTargetContainer(containers, index) {
    if (!Array.isArray(containers) || containers.length === 0) return null;
    const indexed = index >= 0 && index < containers.length ? containers[index] : null;
    if (indexed instanceof HTMLElement && indexed.classList.contains('product-media-container--image')) return indexed;
    const firstImage = containers.find(
      (c) => c instanceof HTMLElement && c.classList.contains('product-media-container--image')
    );
    if (firstImage) return firstImage;
    return indexed || containers[0] || null;
  }

  isTargetSlideActive(targetContainer) {
    if (!(targetContainer instanceof HTMLElement)) return true;
    if (targetContainer.tagName.toLowerCase() !== 'slideshow-slide') return true;
    if (targetContainer.hasAttribute('hidden')) return false;
    return targetContainer.getAttribute('aria-hidden') !== 'true';
  }

  navigateSlideshowToTarget(gallery, gridVisible, nextTarget) {
    if (gridVisible) return;
    const slideshow = gallery.querySelector('slideshow-component[ref="slideshow"]');
    if (!(slideshow instanceof HTMLElement)) return;
    if (!slideshow.contains(nextTarget)) return;
    if (this.isTargetSlideActive(nextTarget)) return;

    const slides = Array.from(slideshow.querySelectorAll('slideshow-slide.product-media-container'));
    const targetIndex = slides.indexOf(nextTarget);
    if (targetIndex < 0) return;

    if (typeof slideshow.select === 'function') {
      try {
        slideshow.select(targetIndex, undefined, { animate: false });
      } catch (_) {}
    }
  }

  isElementVisible(element) {
    if (!(element instanceof Element)) return false;
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    return element.getClientRects().length > 0;
  }

  attachLayer(state, targetContainer) {
    if (state.targetContainer !== targetContainer) {
      this.detachLayer(state);
      state.targetContainer = targetContainer;
    }

    if (!state.targetContainer) return;

    let layer = Array.from(state.targetContainer.children).find(
      (child) => child instanceof HTMLElement && child.classList.contains('joya-preview-layer')
    );

    if (!(layer instanceof HTMLElement)) {
      layer = document.createElement('div');
      layer.className = 'joya-preview-layer';
      state.targetContainer.appendChild(layer);
    }

    let canvas = layer.querySelector('canvas.joya-preview-canvas');
    if (!(canvas instanceof HTMLCanvasElement)) {
      canvas = document.createElement('canvas');
      canvas.className = 'joya-preview-canvas';
      layer.appendChild(canvas);
    }

    state.layer = layer;
    state.canvas = canvas;
  }

  detachLayer(state) {
    this.restoreGridOrder(state);
    if (state.targetImage) {
      state.targetImage.removeEventListener('load', state.onImageLoad);
    }
    state.targetImage = null;
    state.targetContainer = null;
    if (state.layer && state.layer.parentElement) {
      state.layer.parentElement.removeChild(state.layer);
    }
    state.layer = null;
    state.canvas = null;
  }

  render(state) {
    if (!(state.canvas instanceof HTMLCanvasElement) || !(state.targetContainer instanceof HTMLElement)) return;

    const containerRect = state.targetContainer.getBoundingClientRect();
    const width = Math.max(1, Math.round(containerRect.width));
    const height = Math.max(1, Math.round(containerRect.height));
    if (!width || !height) return;

    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const pixelWidth = Math.round(width * dpr);
    const pixelHeight = Math.round(height * dpr);

    if (state.canvas.width !== pixelWidth || state.canvas.height !== pixelHeight) {
      state.canvas.width = pixelWidth;
      state.canvas.height = pixelHeight;
    }

    const ctx = state.canvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const wrapper = state.wrapper;
    const tipoGrabado = wrapper.dataset.tipoGrabado || 'ninguno';
    if (tipoGrabado === 'ninguno') return;

    const imageBounds = this.resolveImageBounds(state, containerRect, width, height);
    const referenceWidth = 800;
    const displayScale = imageBounds.width / referenceWidth;

    const xPct1 = this.clamp(this.toNumber(wrapper.dataset.previewXPct, 50), 0, 100);
    const yPct1 = this.clamp(this.toNumber(wrapper.dataset.previewYPct, 50), 0, 100);
    const xPct2 = this.clamp(this.toNumber(wrapper.dataset.previewX2Pct, xPct1), 0, 100);
    const yPct2 = this.clamp(this.toNumber(wrapper.dataset.previewY2Pct, yPct1), 0, 100);
    const sizePx = Math.max(1, this.toNumber(wrapper.dataset.previewSizePx, 20));
    const color = this.readColor(wrapper.dataset.previewColor, '#333333');
    const rotationDeg = this.toNumber(wrapper.dataset.previewRotationDeg, 0);
    const rotationDeg2 = this.toNumber(wrapper.dataset.previewRotationDeg2, rotationDeg);
    const fontFamily = wrapper.dataset.previewFontFamily || 'TimesNewRomanPS-BoldMT, "Times New Roman", serif';
    const arcEnabled = wrapper.dataset.previewArcEnabled === 'true';
    const arcRadiusPx = Math.max(1, this.toNumber(wrapper.dataset.previewArcRadiusPx, 50));

    // Siempre placa 1, independientemente del número seleccionado
    // ambos → delantera en pos1, trasera en pos2
    // delantera/trasera solo → texto en pos1
    const toRender = [];

    if (tipoGrabado === 'delantera' || tipoGrabado === 'ambos') {
      const text = this.readSideText(wrapper, 'delantera');
      if (text) toRender.push({ text, xPct: xPct1, yPct: yPct1, rotationDeg });
    }
    if (tipoGrabado === 'trasera') {
      const text = this.readSideText(wrapper, 'trasera');
      if (text) toRender.push({ text, xPct: xPct1, yPct: yPct1, rotationDeg });
    }
    if (tipoGrabado === 'ambos') {
      const text = this.readSideText(wrapper, 'trasera');
      if (text) toRender.push({ text, xPct: xPct2, yPct: yPct2, rotationDeg: rotationDeg2 });
    }

    if (toRender.length === 0) return;

    const textAreaWidthPct = this.toNumber(wrapper.dataset.previewTextAreaWidthPct, 0);
    const textAreaHeightPct = this.toNumber(wrapper.dataset.previewTextAreaHeightPct, 0);
    const debugTextArea = wrapper.dataset.previewDebugTextArea === 'true';

    toRender.forEach(({ text, xPct, yPct, rotationDeg: entryRotationDeg }) => {
      const rotationDeg = entryRotationDeg;
      const cx = imageBounds.x + (xPct / 100) * imageBounds.width;
      const cy = imageBounds.y + (yPct / 100) * imageBounds.height;
      const fontSize = Math.max(1, sizePx * displayScale);

      let debugRect = null;

      ctx.save();
      ctx.beginPath();
      if (textAreaWidthPct > 0 && textAreaHeightPct > 0) {
        const areaW = (textAreaWidthPct / 100) * imageBounds.width;
        const areaH = (textAreaHeightPct / 100) * imageBounds.height;
        ctx.rect(cx - areaW / 2, cy - areaH / 2, areaW, areaH);
        if (debugTextArea) {
          debugRect = { x: cx - areaW / 2, y: cy - areaH / 2, w: areaW, h: areaH };
        }
      } else {
        ctx.rect(imageBounds.x, imageBounds.y, imageBounds.width, imageBounds.height);
      }
      ctx.clip();

      const maxWidth = (textAreaWidthPct > 0 && textAreaHeightPct > 0)
        ? (textAreaWidthPct / 100) * imageBounds.width
        : 0;

      if (arcEnabled) {
        this.drawTextOnArc(ctx, {
          text,
          cx,
          cy,
          radius: arcRadiusPx * displayScale,
          rotationDeg,
          fontSize,
          color,
          fontFamily,
        });
      } else {
        this.drawText(ctx, {
          text,
          x: cx,
          y: cy,
          fontSize,
          color,
          rotationDeg,
          fontFamily,
          maxWidth,
        });
      }

      ctx.restore();

      if (debugRect) {
        ctx.save();
        ctx.strokeStyle = 'rgba(255, 0, 0, 0.7)';
        ctx.lineWidth = 2;
        ctx.strokeRect(debugRect.x, debugRect.y, debugRect.w, debugRect.h);
        ctx.restore();
      }
    });
  }

  readSideText(wrapper, side) {
    const sections = Array.from(
      wrapper.querySelectorAll('[data-joya-grabado-section="' + side + '"]')
    ).sort((a, b) => Number(a.dataset.placaIndex || 1) - Number(b.dataset.placaIndex || 1));
    if (sections.length === 0) return '';
    return sections
      .map((section) => {
        const input = section.querySelector('[data-joya-grabado-input]');
        return (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) ? input.value.trim() : '';
      })
      .filter(Boolean)
      .join('\n');
  }

  drawTextOnArc(ctx, { text, cx, cy, radius, rotationDeg, fontSize, color, fontFamily }) {
    const firstLine = text.split(/\r?\n/)[0].trim();
    if (!firstLine) return;

    ctx.save();
    ctx.font = `400 ${fontSize}px ${fontFamily}`;
    ctx.fillStyle = color;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';

    if (document.fonts && !document.fonts.check(ctx.font)) {
      document.fonts.load(ctx.font).then(() => this.renderAll());
    }

    const chars = Array.from(firstLine);
    const widths = chars.map((c) => ctx.measureText(c).width);
    const totalWidth = widths.reduce((a, b) => a + b, 0);
    const totalAngle = totalWidth / radius;
    const centerAngle = (Number(rotationDeg) * Math.PI) / 180;

    // Start from left (higher angle) → go right (lower angle), reads L→R on bottom arc
    let currentAngle = centerAngle + totalAngle / 2;

    chars.forEach((char, i) => {
      const charAngle = currentAngle - widths[i] / 2 / radius;
      ctx.save();
      ctx.translate(cx + radius * Math.cos(charAngle), cy + radius * Math.sin(charAngle));
      ctx.rotate(charAngle - Math.PI / 2);
      ctx.fillText(char, -widths[i] / 2, 0);
      ctx.restore();
      currentAngle -= widths[i] / radius;
    });

    ctx.restore();
  }

  drawText(ctx, { text, x, y, fontSize, color, rotationDeg, fontFamily = 'TimesNewRomanPS-BoldMT, "Times New Roman", serif', maxWidth = 0 }) {
    const rawLines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (rawLines.length === 0) return;

    const rotationRad = (Number(rotationDeg) * Math.PI) / 180;

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(Number.isFinite(rotationRad) ? rotationRad : 0);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = color;
    ctx.font = `400 ${fontSize}px ${fontFamily}`;

    if (document.fonts && !document.fonts.check(ctx.font)) {
      document.fonts.load(ctx.font).then(() => this.renderAll());
    }

    const lines = maxWidth > 0
      ? rawLines.flatMap((l) => this.wrapTextLine(ctx, l, maxWidth))
      : rawLines;

    const lineHeight = fontSize * 1.1;
    const firstLineY = -((lines.length - 1) * lineHeight) / 2;

    lines.forEach((line, index) => {
      ctx.fillText(line, 0, firstLineY + lineHeight * index);
    });

    ctx.restore();
  }

  wrapTextLine(ctx, text, maxWidth) {
    if (!text || ctx.measureText(text).width <= maxWidth) return [text];

    const words = text.split(' ');
    if (words.length > 1) {
      const lines = [];
      let current = '';
      for (const word of words) {
        const test = current ? current + ' ' + word : word;
        if (ctx.measureText(test).width > maxWidth && current) {
          lines.push(current);
          current = word;
        } else {
          current = test;
        }
      }
      if (current) lines.push(current);
      return lines.flatMap((line) => this.wrapTextLine(ctx, line, maxWidth));
    }

    // Single word — character-level wrap
    const chars = Array.from(text);
    const lines = [];
    let current = '';
    for (const char of chars) {
      const test = current + char;
      if (ctx.measureText(test).width > maxWidth && current) {
        lines.push(current);
        current = char;
      } else {
        current = test;
      }
    }
    if (current) lines.push(current);
    return lines;
  }

  enforceTextLimit(state, inputEl) {
    const wrapper = state.wrapper;
    const textAreaWidthPct = this.toNumber(wrapper.dataset.previewTextAreaWidthPct, 0);
    const textAreaHeightPct = this.toNumber(wrapper.dataset.previewTextAreaHeightPct, 0);
    if (!textAreaWidthPct || !textAreaHeightPct) return;
    if (!(state.canvas instanceof HTMLCanvasElement)) return;
    if (!(state.targetContainer instanceof HTMLElement)) return;

    const containerRect = state.targetContainer.getBoundingClientRect();
    const containerWidth = Math.max(1, Math.round(containerRect.width));
    const containerHeight = Math.max(1, Math.round(containerRect.height));
    const imageBounds = this.resolveImageBounds(state, containerRect, containerWidth, containerHeight);

    const sizePx = Math.max(1, this.toNumber(wrapper.dataset.previewSizePx, 20));
    const displayScale = imageBounds.width / 800;
    const fontSize = Math.max(1, sizePx * displayScale);
    const fontFamily = wrapper.dataset.previewFontFamily || 'TimesNewRomanPS-BoldMT, "Times New Roman", serif';

    const areaW = (textAreaWidthPct / 100) * imageBounds.width;
    const areaH = (textAreaHeightPct / 100) * imageBounds.height;
    const lineHeight = fontSize * 1.1;
    const maxLines = Math.max(1, Math.floor(areaH / lineHeight));

    const ctx = state.canvas.getContext('2d');
    if (!ctx) return;
    ctx.font = `400 ${fontSize}px ${fontFamily}`;

    const fits = (value) => {
      const rawLines = value.split(/\r?\n/).map((l) => l.trim());
      const wrapped = rawLines.flatMap((l) => l ? this.wrapTextLine(ctx, l, areaW) : ['']);
      return wrapped.filter(Boolean).length <= maxLines;
    };

    if (fits(inputEl.value)) return;

    let trimmed = inputEl.value;
    while (trimmed.length > 0 && !fits(trimmed)) {
      trimmed = trimmed.slice(0, -1);
    }
    inputEl.value = trimmed;
  }

  resolveImageBounds(state, containerRect, containerWidth, containerHeight) {
    if (!(state.targetImage instanceof HTMLImageElement)) {
      return { x: 0, y: 0, width: containerWidth, height: containerHeight };
    }

    const imageRect = state.targetImage.getBoundingClientRect();
    const rawX = imageRect.left - containerRect.left;
    const rawY = imageRect.top - containerRect.top;
    const rawWidth = imageRect.width;
    const rawHeight = imageRect.height;

    if (rawWidth <= 0 || rawHeight <= 0) {
      return { x: 0, y: 0, width: containerWidth, height: containerHeight };
    }

    const x = this.clamp(rawX, 0, containerWidth);
    const y = this.clamp(rawY, 0, containerHeight);
    const width = Math.max(1, Math.min(rawWidth, containerWidth - x));
    const height = Math.max(1, Math.min(rawHeight, containerHeight - y));

    return { x, y, width, height };
  }

  toNumber(value, fallback) {
    const parsed = Number.parseFloat((value || '').toString());
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  readColor(value, fallback) {
    const normalized = (value || '').toString().trim();
    if (!normalized) return fallback;
    if (window.CSS && typeof window.CSS.supports === 'function' && !window.CSS.supports('color', normalized)) {
      return fallback;
    }
    return normalized;
  }

  clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.joyaPreviewCanvas = new JoyaPreviewCanvas();
});
